import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useEnvironment } from "@keycloak/keycloak-ui-shared";
import { getGroups } from "../../api/methods";
import { Group } from "../../api/representations";
import { Page } from "../../components/page/Page";
import { usePromise } from "../../utils/usePromise";
import {Environment} from "../../environment";
import Card from "../../components/card/Card";
import { Box, Checkbox, FormControlLabel, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { cardContentStyles, loadMoreBoxStyles, loadMoreDescStyles, tableRowStyles } from "./styles";
import Button from "../../components/button/Button";

export const getParents = (el: Group, groups: Group[], groupsPaths: string[]) => {
    const parentPath = el.path.slice(0, el.path.lastIndexOf("/"));
    if (parentPath && !groupsPaths.includes(parentPath)) {
      el = {
        name: parentPath.slice(parentPath.lastIndexOf("/") + 1),
        path: parentPath,
      };
      groups.push(el);
      groupsPaths.push(parentPath);

      getParents(el, groups, groupsPaths);
    }
  };

export const Groups = () => {
  const { t } = useTranslation();
  const context = useEnvironment<Environment>();

  const [groups, setGroups] = useState<Group[]>([]);
  const [displayedGroups, setDisplayedGroups] = useState<Group[]>([]);
  const [directMembership, setDirectMembership] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  const GROUPS_PER_LOAD = 50;

  usePromise(
    (signal) => getGroups({ signal, context }),
    (groups) => {
      if (!directMembership) {
        groups.forEach((el) =>
          getParents(
            el,
            groups,
            groups.map(({ path }) => path),
          ),
        );
      }
      setGroups(groups);

      setDisplayedGroups(groups.slice(0, GROUPS_PER_LOAD));
      setHasMore(groups.length > GROUPS_PER_LOAD);
    },
    [directMembership],
  );

const onLoadMore = () => {
    const newStartIndex = displayedGroups.length;
    const newEndIndex = newStartIndex + GROUPS_PER_LOAD;
    
    const newDisplayedGroups = [
      ...displayedGroups,
      ...groups.slice(newStartIndex, newEndIndex),
    ];
    setDisplayedGroups(newDisplayedGroups);

    if (newDisplayedGroups.length >= groups.length) {
      setHasMore(false);
    }
  };

  const remainingGroupsCount = groups.length - displayedGroups.length;
  const nextLoadCount = Math.min(remainingGroupsCount, GROUPS_PER_LOAD);

  return (
    <Page title={t("groups")}>
      <Card
        title={t("groupsAssign")}
        sxContent={cardContentStyles}
        action={
          <FormControlLabel
            control={
              <Checkbox
                disableRipple
                data-testid="directMembership-checkbox"
                checked={directMembership}
                onChange={(_event, checked) => setDirectMembership(checked)}
                size="small"
              />
            }
            label={t("directMembership")}
          />
        }
        withHeadBorder
      >
        <TableContainer>
          <Table aria-label={t("groups")}>
            <TableHead aria-label={t("groupsListHeader")}>
              <TableRow aria-label={t("groupsListColumnsNames")}>
                <TableCell sx={{ paddingLeft: 0 }}>
                  <strong>{t("name")}</strong>
                </TableCell>
                <TableCell>
                  <strong>{t("path")}</strong>
                </TableCell>
                <TableCell align="center" sx={{ paddingRight: 0 }}>
                  <strong>{t("directMembership")}</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedGroups.map((group, appIndex) => (
                <TableRow
                  id={`${appIndex}-group`}
                  key={"group-" + appIndex}
                  aria-labelledby="groups-list"
                  sx={tableRowStyles}
                >
                  <TableCell
                    component="th"
                    scope="row"
                    data-testid={`group[${appIndex}].name`}
                    sx={{ paddingLeft: 0 }}
                  >
                    {group.name}
                  </TableCell>
                  <TableCell>{group.path}</TableCell>
                  <TableCell align="center" sx={{ paddingRight: 0 }}>
                    <Checkbox
                      size="small"
                      id={`${appIndex}-checkbox-directMembership`}
                      checked={group.id != null}
                      disabled={true}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      {hasMore && (
        <Box
          sx={loadMoreBoxStyles}
        >
          <Button variant="contained" onClick={onLoadMore}>
            {t('loadMore', { 0: nextLoadCount })}
          </Button>
          <Typography sx={loadMoreDescStyles}>
            {t('viewOf', { 0: displayedGroups.length, 1: groups.length })}
          </Typography>
        </Box>
      )}
    </Page>
  );
};

export default Groups;
